import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { get, omit } from 'lodash';
import { makeStyles } from 'src/makeStyles';
import { Divider, IconButton, Menu, MenuItem } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import EditIcon from '@mui/icons-material/Edit';
import { useDispatch, useSelector } from 'react-redux';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import cn from 'src/classnames';

import { SHARE_ACCESS_PLACES, TP, TRANSFORMATION_ARTIST_PROFILE_LISTING } from 'constants/consts';
import ImageTransformation from 'components/Transformation/images';
import { selectProfileIdsWithAccess } from 'containers/App/selectors';
import DeleteFbIcon from 'components/Icons/DeleteFbIcon';
import ShareAccess from 'components/ShareAccess';
import { getOne, create } from 'utils/API';
import { AGENCY_ACTIONS, PROFILE_NOTIFICATIONS } from 'constants/endpointConstants';
import { setActiveProfileAction } from 'containers/App/actions';
import { Router } from 'src/i18n';
import { setCookie } from 'utils/cookie';
import { PROFILE_SWITCH } from 'constants/cookieConstants';
import { selectUserSubscriptions } from 'containers/UserContainers/AccountSettingsContainer/selectors';
import InitialsNameGenerator from '../../../components/InitialsNameGenerator';
import email from '../../../public/images/icons/email.png';
import phone from '../../../public/images/icons/phone.png';
import ImageTag from '../../../components/ImageTag';
import { colors, fontSize } from '../../../constants/styleConstants';
import { useFBTableStyles } from '../../../components/FbTable';
import FbTableCard, { useTableCardStyles } from '../../../components/FbTable/FbTableCard';
import route, { actionsPagesRoutes, artistsPaths } from '../../../constants/routes';
import TextButton from '../../../components/TextButton';
import NextLink from '../../../components/NextLink';
import { getDynamicRoutePropsForNewTab, getRouteWithSlug } from '../../../utils/common';
import { getAgreementCountries } from '../../../utils/agency';
import { AGREEMENTS_TYPES } from '../constants';
import { getAgencyInfoAction } from '../actions';

const useStyles = makeStyles()(theme => ({
  agreementsInfo: {
    textTransform: 'capitalize',
  },
  regionItem: {
    padding: '0px 5px',
    color: '#565656',
    fontSize: fontSize.smallTitle,
    borderRadius: 1.4,
    backgroundColor: `${colors.background.default} !important`,
    fontWeight: 400,
    marginRight: 5,
    display: 'inline-block',
    wordBreak: 'break-word',
  },
  innerCell: {
    borderLeft: `1px solid ${colors.linesGray}`,
  },
  avatar: {
    marginRight: 10,
  },
  artistInfo: {
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      alignItems: 'center',
    },
  },
  artistNameBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    width: '100%',
    padding: `10px`,
  },
  editButtonsBlock: {
    marginLeft: 'auto',
    maxWidth: 80,
    padding: 10,
    display: 'flex',
    textAlign: 'center',
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
      width: '100%',
      justifyContent: 'flex-end',
      marginLeft: '0',
    },
  },
  editButton: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '18px',
    color: colors.blueChip,
    marginRight: 40,
    marginLeft: 40,
    [theme.breakpoints.down('md')]: {
      marginRight: 0,
      marginLeft: 0,
    },
  },
  bold: {
    fontWeight: 'bold',
  },
  label: {
    color: colors.subTitle,
  },
  profession: {
    color: colors.darkText,
    fontSize: fontSize.medium,
    fontWeight: 'bold',
  },
  requestLink: {
    color: 'black',
    whiteSpace: 'nowrap',
  },
  agencyLink: {
    display: 'block',
  },
  greyChip: {
    textTransform: 'uppercase',
    backgroundColor: colors.background.default,
  },
  agreementType: {
    color: colors.cardTitle,
    fontSize: fontSize.smallTitle,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  generalManager: {
    fontWeight: 'bold',
    fontSize: fontSize.medium,
  },
  representative: {
    fontSize: fontSize.medium,
  },
  includeExclude: {
    textTransform: 'capitalize',
  },
  representativeName: {
    fontWeight: 'bold',
    fontSize: fontSize.medium,
  },
  exclusiveTerritories: {
    marginRight: 20,
    [theme.breakpoints.down('lg')]: {
      width: '100%',
    },
  },
  agencyRow: {
    width: '100%',
  },
  cropedImage: {
    height: TRANSFORMATION_ARTIST_PROFILE_LISTING.height,
    width: TRANSFORMATION_ARTIST_PROFILE_LISTING.width,
    borderRadius: TRANSFORMATION_ARTIST_PROFILE_LISTING.radius,
    padding: '3px',
    marginRight: '5px',
  },
  icon: {
    width: '47px',
    height: '47px',
    marginRight: '5px',
  },
  disableLink: {
    pointerEvents: 'none',
    cursor: 'default',
  },
  tableHead: {
    background: colors.lightGrey,
    color: colors.grey1,
    textTransform: 'uppercase',
  },
  iconButton: { marginRight: '5px' },
  shareButton: {
    minWidth: '135px',
    height: '33px',
    background: colors.blueChip,
    border: `1px solid ${colors.blue4}`,
    borderRadius: '8px',
    textTransform: 'uppercase',
    '&:hover': {
      background: colors.blue4,
    },
  },
  addButton: {
    background: '#0067C5',
    width: '95px',
    height: '33px',
    border: '1px solid #005099',
    borderRadius: '8px',
    textTransform: 'uppercase',
    color: 'white',
    fontSize: '16px',
    lineHeight: '19px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  hideButton: {
    background: 'transparent',
    width: '95px',
    height: '33px',
    border: '1px solid #005099',
    borderRadius: '8px',
    textTransform: 'uppercase',
    color: '#0067C5',
    fontSize: '16px',
    lineHeight: '19px',
    fontWeight: 700,
    marginRight: '10px',
    cursor: 'pointer',
    [theme.breakpoints.down('md')]: {
      marginRight: '0px',
      marginBottom: '10px',
    },
  },
  actionPending: {
    minWidth: '135px',
    color: colors.blueChip,
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  more: {
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: '14px',
    padding: '8px',
  },
  moreActive: {
    color: colors.blueChip,
    background: '#DAE3FA',
    borderRadius: '8px',
  },
  arrowIcon: {
    marginLeft: '5px',
    height: '15px',
    width: '15px',
  },
  notification: {
    display: 'flex',
    paddingRight: '15px',
  },
  notificationLink: {
    whiteSpace: 'nowrap',
    color: '#0067C5',
    fontWeight: 600,
    [theme.breakpoints.down('md')]: {
      fontWeight: 600,
      fontSize: '14px',
    },
  },
  notificationNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '5px',
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    background: '#F36D2A',
    color: 'white',
  },
}));

export const AgreementsData = React.memo(({ agreements, t, commonClasses }) => {
  const { classes } = useStyles();
  const generalManagementIndex = agreements.findIndex(
    agr => get(agr, 'agreementType') === AGREEMENTS_TYPES.GENERAL_MENEGMENT,
  );
  const isGeneralManagement = generalManagementIndex !== -1;
  let noGeneralManagementAgreements = agreements;
  if (isGeneralManagement) {
    noGeneralManagementAgreements = agreements.filter(
      agr => get(agr, 'agreementType', '') !== AGREEMENTS_TYPES.GENERAL_MENEGMENT,
    );
  }
  const contactPersons = get(agreements, '0.contactPersons', []);

  return (
    <>
      {isGeneralManagement && (
        <span className={commonClasses.generalManager}>{t(`${TP}.m_EDIT_ROSTER_LIST_GENERALMANAGER`)}</span>
      )}
      {noGeneralManagementAgreements.map(agreement => (
        <div key={get(agreement, 'id')}>
          <span className={commonClasses.agreementType}>{get(agreement, 'agreementType', '')}</span>{' '}
          {get(agreement, 'exclusive') ? (
            <span className={commonClasses.agreementType}>{t(`${TP}.m_TERRITORIES_E`)}</span>
          ) : (
            <span className={commonClasses.agreementType}>{t(`${TP}.m_TERRITORIES_NE`)}</span>
          )}
          {': '}
          {get(agreement, 'includedCountries', []).map(region => (
            <span key={`includedCountries-${get(region, 'id')}`} className={classes.regionItem}>
              {get(region, 'name', '')}
            </span>
          ))}
          {get(agreement, 'exclusive') && get(agreement, 'excludedCountries', []).length > 0 && (
            <>
              {`${t(`${TP}.REPRESENTATION_EXCLUDING`)}: `}
              {get(agreement, 'excludedCountries', []).map(region => (
                <span key={`excludedCountries-${get(region, 'id')}`} className={classes.regionItem}>
                  {get(region, 'name', '')}
                </span>
              ))}
            </>
          )}
        </div>
      ))}
      {contactPersons.length > 0 && (
        <div>
          <span className={commonClasses.representative}>{t(`${TP}.FN_AGENCY_REPRESENTATIVE`)}: </span>
          {contactPersons.map((el, index) => (
            <span className={commonClasses.representativeName}>
              {el.agentName}
              {contactPersons[index + 1] ? ', ' : ''}
            </span>
          ))}
        </div>
      )}
    </>
  );
});

export const AgreementsDataV1 = React.memo(
  ({ agreements, t, professionsAgreement, agencyProfessioMeta, commonClasses, contactPersons }) => {
    const { classes } = useStyles();

    const agreementIdList = agreements?.map(({ connection: { id } }) => id);

    const agreementList = Object.keys(professionsAgreement)
      ?.map(key => professionsAgreement[key])
      ?.map(aAgreement =>
        aAgreement
          ?.map(agreement => ({
            ...agreement,
            ...getAgreementCountries(agreement),
          }))
          ?.filter(
            agr =>
              agreementIdList?.includes(agr?.connection?.id) &&
              agr?.agreementType !== AGREEMENTS_TYPES.GENERAL_MENEGMENT,
          ),
      );

    return (
      <>
        <TableContainer>
          <Table className={classes.table} size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>{t(`${TP}.FN_PROFESSION_VOICE_TYPE`)}</TableCell>
                <TableCell>{t(`${TP}.FN_REPRESENTATIVE_INFO`)}</TableCell>
                <TableCell>{t(`${TP}.m_REPRESENTATIONDETAIL`)}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* {isGeneralManagement && (
                <span className={commonClasses.bold}>{t(`${TP}.m_EDIT_ROSTER_LIST_GENERALMANAGER`)}</span>
              )} */}
              {agreementList?.map(agr => (
                <>
                  <TableCell>
                    {agr?.length > 0 && (
                      <>
                        <div className={classes.profession}>
                          {agencyProfessioMeta?.[agr?.[0]?.agencyConnectionProfession?.id]?.name ?? '-'}
                        </div>
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {contactPersons.length > 0 && (
                      <div className={classes.agentDetails}>
                        {/* <p className={classes.title}>{t(`${TP}.AGENTS_REPRESENTATIVES`)} </p> */}
                        {contactPersons.map(cp => (
                          <div className={classes.agentProfile}>
                            {cp?.profile?.image && <ImageTag src={cp?.profile?.image} alt="profile" />}
                            <span>{cp?.agentName}</span>
                            {cp.agentPhoneNumber && (
                              <div className={classes.repInfo}>
                                <ImageTag src={phone} alt="phone" />
                                {cp.agentPhoneNumber}
                              </div>
                            )}
                            {cp.agentEmail && (
                              <div className={classes.repInfo}>
                                <ImageTag src={email} alt="email" />
                                <a href={`mailto:${cp.agentEmail}`}>{cp.agentEmail}</a>
                              </div>
                            )}
                          </div>
                        ))}
                        {/* <ContactLinks contacts={agency?.agent?.profile?.contacts} /> */}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className={classes.representationDetailsWrapper}>
                      <div className={classes.representationDetailsTerritories}>
                        {agr?.map(agreement => (
                          <div className={commonClasses.agreementsInfo} key={get(agreement, 'id')}>
                            <div className={classes.exclusiveTerritories}>
                              <span className={commonClasses.label}>
                                {`"${get(agreement, 'agreementType', '')}"`}{' '}
                                {get(agreement, 'exclusive') ? t(`${TP}.m_TERRITORIES_E`) : t(`${TP}.m_TERRITORIES_NE`)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className={classes.includeWrapper}>
                        {agr?.map(agreement => (
                          <div className={commonClasses.agreementsInfo} key={get(agreement, 'id')}>
                            <div className={classes.countries}>
                              <span className={classes.includeExclude}>{`${t(`${TP}.REPRESENTATION_INCLUDE`)} `}</span>
                              {get(agreement, 'includedCountries', []).map(region => (
                                <>
                                  <span className={classes.regionItem} key={`includedCountries-${get(region, 'id')}`}>
                                    {get(region, 'name', '')}
                                  </span>
                                </>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className={classes.excludeWrapper}>
                        {agr?.map(agreement => (
                          <div className={commonClasses.agreementsInfo} key={get(agreement, 'id')}>
                            {get(agreement, 'exclusive') && get(agreement, 'excludedCountries', []).length > 0 && (
                              <>
                                <span className={classes.includeExclude}>{`${t(
                                  `${TP}.REPRESENTATION_EXCLUDING`,
                                )} `}</span>
                                {get(agreement, 'excludedCountries', []).map(region => (
                                  <span className={classes.regionItem} key={`excludedCountries-${get(region, 'id')}`}>
                                    {get(region, 'name', '')}
                                  </span>
                                ))}
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </TableCell>
                </>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  },
);

const addAgreementPageUrl = (agencyUrl, artistData) => ({
  href: `${agencyUrl}/${actionsPagesRoutes.ADD_AGREEMENTS}?artist_id=${get(artistData, 'id')}`,
});

const ArtistInfo = React.memo(({ data, classes, t }) => {
  const { cx } = useStyles();
  return (
    <div className={classes.artistInfo}>
      {get(data, 'artist.profile.image.small') ? (
        <ImageTransformation
          transformation={TRANSFORMATION_ARTIST_PROFILE_LISTING}
          src={get(data, 'artist.profile.image.small')}
          alt={get(data, 'artist.profile.name') || t(`${TP}.m_NO_VALUE}`)}
          className={classes.cropedImage}
        />
      ) : (
        <InitialsNameGenerator className={classes.icon} userName={get(data, 'artist.profile.name')} />
      )}
      {get(data, 'artist.profile.id') ? (
        <NextLink
          className={cx({ [classes.disableLink]: get(data, 'artist.profile.is_hidden') })}
          href={`${route.ARTISTS}/[id]`}
          as={getRouteWithSlug(route.ARTISTS, get(data, 'artist.profile'))}
        >
          {get(data, 'artist.profile.name', '')}
        </NextLink>
      ) : (
        get(data, 'artist.profile.name', '')
      )}
    </div>
  );
});

const EditButtons = ({
  deleteFunction,
  classes,
  agreementPageLinkProps,
  targetProfile,
  userProfiles,
  onRequest,
  mobile,
  t,
}) => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(false);
  const profileIdsWithAccess = useSelector(selectProfileIdsWithAccess());
  const { data: userSubscriptions } = useSelector(selectUserSubscriptions());
  const profileAccess = useMemo(() => userProfiles.find(profile => profile.id === targetProfile?.id), [
    targetProfile,
    userProfiles,
  ]);
  const pendingStatus = useMemo(() => profileIdsWithAccess.includes(targetProfile?.id), [
    profileIdsWithAccess,
    targetProfile,
  ]);
  const [notificationsNumbers, setNotificationsNumbers] = useState(0);

  useEffect(() => {
    if (targetProfile) {
      getOne(PROFILE_NOTIFICATIONS, targetProfile?.id).then(response => {
        if (response?.data) {
          // Removed as we are not using it on V3 TODO: Change this on V4 version
          const profileNotifications = omit(response?.data, ['changeRequest', 'profileAccessRequests', 'redMask']);
          setNotificationsNumbers(
            Object.values(profileNotifications).reduce((acc, item) => {
              const count = acc + item;
              return count;
            }, 0),
          );
        }
      });
    }
  }, [setNotificationsNumbers, targetProfile]);

  const handleOpenMenu = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleAction = () => {
    const profileSubscription = userSubscriptions.find(subscription => subscription?.profile?.id === targetProfile?.id);
    const completeurl = window.location.href;
    if (profileSubscription) {
      window.location.href = `${route.REACTIVATE}?subid=${profileSubscription.id}&profileid=${targetProfile?.id}&backurl=${completeurl}`;
    } else {
      const URLParams = new URLSearchParams({
        subid: 6,
        completeurl,
        profileid: targetProfile?.id,
      });
      window.location.href = `${route.PAYMENT}?${URLParams.toString()}`;
    }
  };

  const handlerViewNotification = () => {
    if (!profileAccess) {
      onRequest(targetProfile);
      return;
    }
    if (profileAccess && targetProfile?.params?.isPro && !pendingStatus) {
      setCookie(PROFILE_SWITCH, true);
      dispatch(
        setActiveProfileAction({
          profileId: targetProfile?.id,
          path: artistsPaths.edit,
          as: `${getRouteWithSlug(route.ARTISTS, targetProfile)}/edit`,
        }),
      );
    } else {
      Router.push({
        pathname: `${getRouteWithSlug(route.ARTISTS, targetProfile)}`,
      });
    }
  };

  return (
    <>
      {notificationsNumbers > 0 && (
        <TextButton className={classes.notification} onClick={handlerViewNotification}>
          <span className={classes.notificationLink}>{t(`${TP}.VIEW_UPDATES`)}</span>
          <span className={classes.notificationNumber}>{notificationsNumbers < 100 ? notificationsNumbers : 99}</span>
        </TextButton>
      )}
      <NextLink
        href={agreementPageLinkProps.href}
        as={agreementPageLinkProps.as}
        type="text"
        className={classes.editButton}
      >
        <EditIcon className={classes.iconButton} />
        {!mobile && t(`${TP}.m_EDIT`)}
      </NextLink>
      {!mobile && (
        <>
          {profileAccess && !pendingStatus && targetProfile?.params?.isPro && (
            <ShareAccess
              className={classes.shareButton}
              entityType="profile"
              entity={targetProfile}
              button
              hubspotType={SHARE_ACCESS_PLACES.AGENCY_PAGE_ARTIST}
            />
          )}

          {profileAccess && !pendingStatus && !targetProfile?.params?.isPro && (
            <button onClick={handleAction} type="button" className={classes.actionPending}>
              {t(`${TP}.ACTION_PENDING`)}
              <ArrowForwardIosIcon className={classes.arrowIcon} />
            </button>
          )}

          {(!profileAccess || (pendingStatus && targetProfile?.params?.isPro)) && (
            <TextButton
              className={classes.requestLink}
              onClick={() => {
                onRequest(targetProfile);
              }}
              disabled={pendingStatus}
            >
              <GroupAddIcon className={classes.iconButton} />
              {t(`${TP}.ACCESS_REQUEST_SEND`)}
            </TextButton>
          )}

          <IconButton onClick={deleteFunction}>
            <DeleteFbIcon color="black" />
          </IconButton>
        </>
      )}
      {mobile && (
        <>
          <button
            onClick={handleOpenMenu}
            type="button"
            className={cn(classes.more, { [classes.moreActive]: Boolean(anchorEl) })}
          >
            <MoreVertIcon /> {t(`${TP}.MORE`)}
          </button>

          <Menu id="demo-customized-menu" anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
            {profileAccess && !pendingStatus && !targetProfile?.params?.isPro && (
              <>
                <MenuItem>
                  <button onClick={handleAction} type="button" className={classes.actionPending}>
                    {t(`${TP}.ACTION_PENDING`)}
                    <ArrowForwardIosIcon className={classes.arrowIcon} />
                  </button>
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
              </>
            )}

            {profileAccess && !pendingStatus && targetProfile?.params?.isPro && (
              <>
                <MenuItem>
                  <ShareAccess
                    className={classes.shareButton}
                    entityType="profile"
                    entity={targetProfile}
                    button
                    hubspotType={SHARE_ACCESS_PLACES.AGENCY_PAGE_ARTIST}
                  />
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
              </>
            )}

            {(!profileAccess || (pendingStatus && targetProfile?.params?.isPro)) && (
              <>
                <MenuItem>
                  <TextButton
                    className={classes.requestLink}
                    onClick={() => {
                      onRequest(targetProfile);
                    }}
                    disabled={pendingStatus}
                  >
                    <GroupAddIcon className={classes.iconButton} />
                    {t(`${TP}.ACCESS_REQUEST_SEND`)}
                  </TextButton>
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
              </>
            )}

            <MenuItem>
              <TextButton className={classes.requestLink} onClick={deleteFunction} disabled={pendingStatus}>
                <DeleteFbIcon color="black" /> {t(`${TP}.m_DELETE`)}
              </TextButton>
            </MenuItem>
          </Menu>
        </>
      )}
    </>
  );
};

const AddButtons = ({ classes, artistProfiles, t }) => {
  const dispatch = useDispatch();
  const { agency } = artistProfiles;

  const handleHide = () => {
    create(AGENCY_ACTIONS(agency.id), {
      id: artistProfiles.id,
      type: 'update-agency-connection-status',
      displayStatus: 2,
      actor: 'agency',
    });
    dispatch(getAgencyInfoAction({ agencyId: agency.id }));
  };

  const handleAdd = () => {
    create(AGENCY_ACTIONS(agency.id), {
      id: artistProfiles.id,
      type: 'update-agency-connection-status',
      displayStatus: 1,
      actor: 'agency',
    });
    dispatch(getAgencyInfoAction({ agencyId: agency.id }));
  };

  return (
    <>
      <button onClick={handleHide} type="button" className={classes.hideButton}>
        {t(`${TP}.FN_HIDE`)}
      </button>

      <button onClick={handleAdd} type="button" className={classes.addButton}>
        {t(`${TP}.m_ADD`)}
      </button>
    </>
  );
};

export const ArtistsRow = ({
  data,
  showAdditionalAgents,
  editMode,
  addMode,
  t,
  deleteArtistClick,
  agencyUrl,
  userProfiles,
  artistProfiles,
  onRequest,
}) => {
  const { classes, cx } = useFBTableStyles();
  const { classes: commonClasses } = useStyles();

  return (
    <TableRow key={get(data, 'id')}>
      <TableCell className={cx(classes.cell, classes.artistName)}>
        <div className={classes.flexInlineCell}>
          <ArtistInfo t={t} data={data} classes={commonClasses} />
        </div>
      </TableCell>
      <TableCell className={cx(classes.cell, commonClasses.innerCell)}>
        <AgreementsData agreements={get(data, 'agreements', [])} t={t} commonClasses={commonClasses} />
      </TableCell>
      {showAdditionalAgents && (
        <TableCell className={cx(classes.cell, commonClasses.innerCell)} style={{ width: '20%' }}>
          {get(data, 'artist.profile.agencies', [])?.map(agency => (
            <NextLink
              className={commonClasses.agencyLink}
              key={agency?.id}
              {...getDynamicRoutePropsForNewTab(route.MANAGERS, agency)}
            >
              {agency?.name}
            </NextLink>
          ))}
        </TableCell>
      )}

      {addMode && (
        <TableCell style={{ width: '10%', minWidth: '340px' }}>
          <div className={classes.flexInlineCell} style={{ justifyContent: 'flex-end' }}>
            <AddButtons artistProfiles={data} classes={commonClasses} t={t} />
          </div>
        </TableCell>
      )}

      {editMode && (
        <TableCell style={{ width: '10%', minWidth: '340px' }}>
          <div className={classes.flexInlineCell} style={{ justifyContent: 'flex-end' }}>
            <EditButtons
              targetProfile={artistProfiles?.find(item => item.id === data?.artist?.profile?.id)}
              userProfiles={userProfiles}
              onRequest={onRequest}
              deleteFunction={() => deleteArtistClick(data.id)}
              classes={commonClasses}
              agreementPageLinkProps={addAgreementPageUrl(agencyUrl, data)}
              t={t}
            />
          </div>
        </TableCell>
      )}
    </TableRow>
  );
};

ArtistsRow.propTypes = {
  data: PropTypes.object,
  showAdditionalAgents: PropTypes.bool,
  editMode: PropTypes.bool,
  agencyUrl: PropTypes.string,
};

export const ArtistsCard = ({
  data,
  showAdditionalAgents,
  editMode,
  addMode,
  t,
  deleteArtistClick,
  agencyUrl,
  artistProfiles,
  userProfiles,
  onRequest,
}) => {
  const { classes } = useTableCardStyles();
  const { classes: commonClasses } = useStyles();

  return (
    <FbTableCard key={get(data, 'id')}>
      <div className={classes.mainInfoElements}>
        <div className={commonClasses.artistNameBlock}>
          <ArtistInfo t={t} data={data} classes={commonClasses} />

          {addMode && (
            <div className={commonClasses.editButtonsBlock}>
              <AddButtons artistProfiles={data} classes={commonClasses} t={t} />
            </div>
          )}
          {editMode && (
            <div className={commonClasses.editButtonsBlock}>
              <EditButtons
                targetProfile={artistProfiles?.find(item => item.id === data?.artist?.profile?.id)}
                userProfiles={userProfiles}
                deleteFunction={() => deleteArtistClick(data.id)}
                classes={commonClasses}
                agreementPageLinkProps={addAgreementPageUrl(agencyUrl, data)}
                mobile
                onRequest={onRequest}
                t={t}
              />
            </div>
          )}
        </div>
      </div>

      <div className={classes.infoElement}>
        <span className={classes.infoTitle}>{t(`${TP}.m_REPRESENTATIONDETAIL`)}</span>
        <AgreementsData agreements={get(data, 'agreements', [])} t={t} commonClasses={commonClasses} />
      </div>

      {showAdditionalAgents && (
        <div className={classes.infoElement}>
          <span className={classes.infoTitle}>{t(`${TP}.m_OTHERMANAGERSOFTHISARTIST`)}</span>
          {get(data, 'artist.profile.agencies', [])?.map(agency => (
            <NextLink
              className={commonClasses.agencyLink}
              key={agency?.id}
              {...getDynamicRoutePropsForNewTab(route.MANAGERS, agency)}
            >
              {agency?.name}
            </NextLink>
          ))}
        </div>
      )}
    </FbTableCard>
  );
};

ArtistsCard.propTypes = {
  data: PropTypes.object,
  showAdditionalAgents: PropTypes.bool,
  editMode: PropTypes.bool,
  agencyUrl: PropTypes.string,
};
